import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Search } from '@mui/icons-material'
import { Autocomplete, FilterOptionsState, TextField, createFilterOptions } from '@mui/material'

interface Props {
  value: string
  liveEventNames: string[]
  onChange?: (searchResult: string) => void
}

const LiveEventsEventSearchAutocomplete: React.FC<Props> = ({ value, onChange, liveEventNames }) => {
  const { t } = useTranslation()
  const autocompleteRef = useRef<HTMLDivElement>(null)
  // Tracks the displayed value of the input so we can recover it when MUI
  // internally passes `inputValue = ''` to `filterOptions` after a selection.
  // See https://github.com/mui/material-ui/issues/19797
  const lastInputValueRef = useRef<string>('')

  const handleChange = useCallback((e: React.SyntheticEvent, value: string | null) => {
    onChange && onChange(value || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (onChange) {
      onChange('')
    }
    if (autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector('input')
      if (input) {
        input.value = ''
      }
    }
    lastInputValueRef.current = ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEventNames])

  const defaultFilter = useMemo(() => createFilterOptions<string>(), [])

  // Workaround for https://github.com/mui/material-ui/issues/19797
  // After selecting an option, MUI sets `inputValueIsSelectedValue = true` and
  // passes `inputValue = ''` to `filterOptions`, which would otherwise show ALL
  // options. We recover the actual input string from a ref so the suggestion
  // list keeps matching what the user sees in the textbox.
  const handleFilterOptions = useCallback(
    (options: string[], state: FilterOptionsState<string>) => {
      const effectiveInput = state.inputValue === '' ? lastInputValueRef.current : state.inputValue
      return defaultFilter(options, { ...state, inputValue: effectiveInput })
    },
    [defaultFilter]
  )

  const handleInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string) => {
    lastInputValueRef.current = newInputValue
  }, [])

  return (
    <div className="LiveEventsEventSearchAutocomplete">
      <Autocomplete
        ref={autocompleteRef}
        value={value}
        fullWidth
        freeSolo
        blurOnSelect
        size="small"
        onChange={handleChange}
        onInputChange={handleInputChange}
        filterOptions={handleFilterOptions}
        options={liveEventNames}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <>
                <Search sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t('live-events:live_events_event_name_filter_title')}
              </>
            }
          />
        )}
      />
    </div>
  )
}

export default LiveEventsEventSearchAutocomplete
